import React, { useContext } from 'react';
import axios from 'axios';
import { HOSTNAME, IS_CLIENT } from '@constants';
import { SUPPORT_LANGS, PRIMARY_LANG } from '@constants/langs';
import { TRANSLATIONS_PATTERNS } from '@constants/params';
import { generateRandomString } from '@modules/helper';

let instance = null;

export const Context = React.createContext({});

export const TrProvider = ({ children, translations }) => {
  if (IS_CLIENT) {
    return children;
  }

  return <Context.Provider value={translations}>{children}</Context.Provider>;
};

export const useTranslations = () => {
  if (IS_CLIENT) {
    return new Translations();
  }

  return useContext(Context);
};

export class Translations {
  constructor({ translation = {} } = {}) {
    if (IS_CLIENT && !instance) {
      instance = this;
    }

    this.translation = translation ? translation : {};

    return instance;
  }

  /**
   * Присоединение ранее полученных переводов
   * @param {Object} translation - объект с переводами
   */
  connect(translation = {}) {
    this.translation = translation;
  }

  /**
   * Загрузка переводов
   * @returns {Object} - объект переводов
   */
  async load(lang) {
    const url =
      HOSTNAME === 'test.sb.planoplan.ru'
        ? `https://files.pp.ksdev.ru/upload/i18n/${lang}.json?v${generateRandomString(10)}`
        : `https://files.${HOSTNAME}/upload/i18n/${lang}.json?v${generateRandomString(8)}`;
    try {
      const response = await axios.get(url);
      const { data } = response;

      for (const item in data) {
        TRANSLATIONS_PATTERNS.forEach((pattern) => {
          const regex = new RegExp(pattern);
          const isOwnProperty = Object.prototype.hasOwnProperty.call(data, item);

          if (isOwnProperty && regex.test(item)) {
            this.translation[item] = data[item];
          }
        });
      }
    } catch (e) {
      this.translation = {};
    }

    return this.translation;
  }

  /**
   * Изменение текущего языка
   * @param {String} value - новая локаль
   * @param {Object} router - объект роутера
   */
  changeLang(value, router) {
    const lang = SUPPORT_LANGS.find((item) => item.value === value) || PRIMARY_LANG;
    const locale = lang.value;
    const { pathname, asPath } = router;
    const href = pathname === '/' && locale === PRIMARY_LANG.value ? asPath : `/${locale}${asPath}`;

    window.location.href = window.location.origin + href;
  }

  /**
   * Получение текста по ключу
   * @param {String} key - ключ, по которому требуется получить текст
   * @returns {String} - текст
   */
  t(key) {
    return this.translation[key] || key;
  }

  tString(string) {
    const rg = /(\{\{.+?\}\})/g;

    if (typeof string === 'string' || string instanceof String) {
      return string.replace(rg, (res) => this.t(res.slice(2, -2)));
    }

    return string;
  }

  tObject(object) {
    for (const key in object) {
      if (
        Object.prototype.hasOwnProperty.call(object, key) &&
        (typeof object[key] === 'string' || object[key] instanceof String)
      ) {
        object[key] = this.tString(object[key]);
      }
    }
  }

  tObjectInArray(objects) {
    objects.forEach((object) => {
      this.tObject(object);
    });
  }

  /**
   * Получение JSON по ключу
   * @param {String} key - ключ по которому требуется получить текст
   * @returns {String} - текст
   */
  json(key) {
    try {
      return JSON.parse(this.translation[key]);
    } catch (e) {
      return null;
    }
  }
}
